import { Injectable } from '@angular/core';

/**
 * LockStateService — the one owner of this tab's "screen locked" flag (B052 / TC-0075 / TC-0076 / TC-0168).
 *
 * The flag lives in sessionStorage (same key as before, so a locked tab stays locked across F5). While it is set,
 * <body> carries the class 'mt-locked', and the global rule in styles.scss hides everything drawn OUTSIDE the app
 * shell (network panels, toasts, pop-ups, the spinner, the PWA banners) until unlock.
 *
 * Only an unlock, a sign-out or a fresh password/MFA sign-in may clear the flag. Never clear it on navigation or on
 * the login page — during start-up renewal the user is briefly not signed in (auth.service.ts:146-148) and clearing
 * then would unlock the tab.
 */
@Injectable({ providedIn: 'root' })
export class LockStateService {
  static readonly STORAGE_KEY = 'meditrack_locked';
  static readonly BODY_CLASS = 'mt-locked';
  private static readonly USER_KEY = 'meditrack_user';   // = AuthService.AUTH_KEY

  constructor() {
    if (!this.isLocked) return;
    let user: string | null = null;
    try { user = sessionStorage.getItem(LockStateService.USER_KEY); } catch {}
    // A flag with nobody signed in is stale (left by an older build that never cleared it) — drop it, so the next
    // person to sign in on this tab does not start on a lock screen.
    if (user) this.addBodyClass();
    else this.clear();
  }

  get isLocked(): boolean {
    try { return sessionStorage.getItem(LockStateService.STORAGE_KEY) === '1'; } catch { return false; }
  }

  lock(): void {
    try { sessionStorage.setItem(LockStateService.STORAGE_KEY, '1'); } catch {}
    this.addBodyClass();
  }

  clear(): void {
    try { sessionStorage.removeItem(LockStateService.STORAGE_KEY); } catch {}
    this.removeBodyClass();
  }

  /** Used when the app shell (and its overlay) leaves the screen; the flag stays so the lock returns with the shell. */
  removeBodyClass(): void {
    try { document.body.classList.remove(LockStateService.BODY_CLASS); } catch {}
  }

  private addBodyClass(): void {
    try { document.body.classList.add(LockStateService.BODY_CLASS); } catch {}
  }
}
